import { c as _c } from "react/compiler-runtime";
// Tremor Raw Tooltip [v0.0.0]
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import React from "react";
import { cx } from "@/lib/tailwind-utils";
interface TooltipProps extends Omit<TooltipPrimitives.TooltipContentProps, "content" | "onClick">, Pick<TooltipPrimitives.TooltipProps, "open" | "defaultOpen" | "onOpenChange" | "delayDuration"> {
  content: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  side?: "bottom" | "left" | "top" | "right";
  showArrow?: boolean;
  triggerAsChild?: boolean;
  triggerOnlyHover?: boolean;
  variant?: "default" | "light";
}

// Elements that already take keyboard focus. Disabled controls are left out on
// purpose: a tooltip explaining why a button is disabled needs its own tab stop.
const FOCUSABLE_SELECTOR = ["a[href]", "button:not(:disabled)", "input:not(:disabled)", "select:not(:disabled)", "textarea:not(:disabled)", '[tabindex]:not([tabindex="-1"])'].join(",");
const Tooltip = React.forwardRef<React.ElementRef<typeof TooltipPrimitives.Content>, TooltipProps>((t0, forwardedRef) => {
  const $ = _c(52);
  let children;
  let className;
  let content;
  let defaultOpen;
  let delayDuration;
  let onClick;
  let onOpenChange;
  let open;
  let props;
  let side;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      children,
      className,
      content,
      delayDuration,
      defaultOpen,
      open,
      onClick,
      onOpenChange,
      showArrow: t1,
      side,
      sideOffset: t2,
      triggerAsChild: t3,
      triggerOnlyHover: t4,
      variant: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = content;
    $[4] = defaultOpen;
    $[5] = delayDuration;
    $[6] = onClick;
    $[7] = onOpenChange;
    $[8] = open;
    $[9] = props;
    $[10] = side;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    children = $[1];
    className = $[2];
    content = $[3];
    defaultOpen = $[4];
    delayDuration = $[5];
    onClick = $[6];
    onOpenChange = $[7];
    open = $[8];
    props = $[9];
    side = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  const showArrow = t1 === undefined ? true : t1;
  const sideOffset = t2 === undefined ? 10 : t2;
  const triggerAsChild = t3 === undefined ? false : t3;
  const triggerOnlyHover = t4 === undefined ? false : t4;
  const variant = t5 === undefined ? "default" : t5;
  const isLight = variant === "light";
  const wrapperRef = React.useRef(null);
  const [hasFocusableChild, setHasFocusableChild] = React.useState(false);
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setHasFocusableChild(!!wrapperRef.current?.querySelector(FOCUSABLE_SELECTOR));
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  React.useEffect(t6);
  if (!content) {
    let t7;
    if ($[17] !== children || $[18] !== onClick || $[19] !== triggerAsChild) {
      t7 = triggerAsChild ? children : <span onClick={onClick}>{children}</span>;
      $[17] = children;
      $[18] = onClick;
      $[19] = triggerAsChild;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t7;
  if ($[21] !== children || $[22] !== hasFocusableChild || $[23] !== triggerAsChild) {
    t7 = triggerAsChild ? children : <span ref={wrapperRef} tabIndex={hasFocusableChild ? -1 : 0} className="inline-block cursor-pointer text-center">{children}</span>;
    $[21] = children;
    $[22] = hasFocusableChild;
    $[23] = triggerAsChild;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== onClick || $[26] !== t7) {
    t8 = <TooltipPrimitives.Trigger onClick={onClick} asChild={true}>{t7}</TooltipPrimitives.Trigger>;
    $[25] = onClick;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = isLight ? "z-[9999] min-w-0 max-w-xs select-none overflow-hidden rounded-lg border border-border bg-background/60 px-3 py-2 shadow-lg backdrop-blur-lg" : "z-[1000] -mb-1.5 max-w-60 select-none overflow-hidden text-ellipsis rounded-md px-2.5 py-1.5 text-body leading-5 shadow-md";
  const t10 = isLight ? "text-foreground" : "text-gray-50";
  const t11 = !isLight && "bg-gray-900 dark:bg-gray-700";
  let t12;
  if ($[28] !== className || $[29] !== t10 || $[30] !== t11 || $[31] !== t9) {
    t12 = cx(t9, t10, t11, "will-change-[transform,opacity]", "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide", className);
    $[28] = className;
    $[29] = t10;
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== isLight || $[34] !== showArrow) {
    t13 = showArrow ? <TooltipPrimitives.Arrow className={cx("z-[1000] border-none", isLight ? "fill-background" : "fill-gray-900 dark:fill-gray-700")} width={12} height={7} aria-hidden="true" /> : null;
    $[33] = isLight;
    $[34] = showArrow;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== content || $[37] !== forwardedRef || $[38] !== props || $[39] !== side || $[40] !== sideOffset || $[41] !== t12 || $[42] !== t13) {
    t14 = <TooltipPrimitives.Portal><TooltipPrimitives.Content ref={forwardedRef} side={side} sideOffset={sideOffset} align="center" className={t12} {...props}>{content}{t13}</TooltipPrimitives.Content></TooltipPrimitives.Portal>;
    $[36] = content;
    $[37] = forwardedRef;
    $[38] = props;
    $[39] = side;
    $[40] = sideOffset;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== defaultOpen || $[45] !== delayDuration || $[46] !== onOpenChange || $[47] !== open || $[48] !== t14 || $[49] !== t8 || $[50] !== triggerOnlyHover) {
    t15 = <TooltipPrimitives.Provider delayDuration={150}><TooltipPrimitives.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} delayDuration={delayDuration} disableHoverableContent={triggerOnlyHover}>{t8}{t14}</TooltipPrimitives.Root></TooltipPrimitives.Provider>;
    $[44] = defaultOpen;
    $[45] = delayDuration;
    $[46] = onOpenChange;
    $[47] = open;
    $[48] = t14;
    $[49] = t8;
    $[50] = triggerOnlyHover;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
});
Tooltip.displayName = "Tooltip";
export default Tooltip;
export { type TooltipProps };